<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['close', 'createAgent']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const agents = useMapGetter('assistantAgents/getRecords');

const currentAgentId = computed(() => route.params.agentId);

const isAgentActive = agent => {
  return agent.id === Number(currentAgentId.value);
};

const handleAgentChange = async agent => {
  if (isAgentActive(agent)) return;

  const targetRouteName = route.name || 'assistant_agents_scenarios_index';

  await router.push({
    name: targetRouteName,
    params: {
      accountId: route.params.accountId,
      agentId: agent.id,
    },
  });

  emit('close');
};

const openCreateAgentDialog = () => {
  emit('createAgent');
  emit('close');
};
</script>

<template>
  <div
    class="pt-5 pb-3 bg-n-alpha-3 backdrop-blur-[100px] outline outline-n-container outline-1 z-50 absolute w-[27.5rem] rounded-xl shadow-md flex flex-col gap-4"
  >
    <div
      class="flex items-center justify-between gap-4 px-6 pb-3 border-b border-n-alpha-2"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <h2
            class="text-base font-medium cursor-pointer text-n-slate-12 w-fit hover:underline"
          >
            {{ t('ASSISTANT.AGENT_SWITCHER.AGENTS') }}
          </h2>
        </div>
        <p class="text-sm text-n-slate-11">
          {{ t('ASSISTANT.AGENT_SWITCHER.SWITCH_AGENT') }}
        </p>
      </div>
      <Button
        :label="t('ASSISTANT.AGENT_SWITCHER.NEW_AGENT')"
        color="slate"
        icon="i-lucide-plus"
        size="sm"
        class="!bg-n-alpha-2 hover:!bg-n-alpha-3"
        @click="openCreateAgentDialog"
      />
    </div>
    <div v-if="agents.length > 0" class="flex flex-col gap-2 px-4">
      <Button
        v-for="agent in agents"
        :key="agent.id"
        :label="agent.name"
        variant="ghost"
        color="slate"
        trailing-icon
        :icon="isAgentActive(agent) ? 'i-lucide-check' : ''"
        class="!justify-end !px-2 !py-2 hover:!bg-n-alpha-2 [&>.i-lucide-check]:text-n-teal-10 h-9"
        size="sm"
        @click="handleAgentChange(agent)"
      >
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ agent.name || '' }}
        </span>
        <Avatar
          v-if="agent"
          :name="agent.name"
          :size="20"
          icon-name="i-lucide-bot"
          rounded-full
        />
      </Button>
    </div>
    <div v-else class="flex flex-col items-center gap-2 px-4 py-3">
      <p class="text-sm text-n-slate-11">
        {{ t('ASSISTANT.AGENT_SWITCHER.EMPTY_LIST') }}
      </p>
    </div>
  </div>
</template>
